export type RangePreset = 'D' | 'W' | 'M' | 'Q' | 'Y' | 'C';

export const RANGE_PRESETS: { key: RangePreset; label: string; days: number }[] = [
  { key: 'D', label: 'Day', days: 1 },
  { key: 'W', label: 'Week', days: 7 },
  { key: 'M', label: 'Month', days: 30 },
  { key: 'Q', label: 'Quarter', days: 92 },
  { key: 'Y', label: 'Year', days: 365 },
  { key: 'C', label: 'Custom', days: 30 },
];

/**
 * The range a preset covers. With `maxLookbackDays` it never reaches past
 * retention — the API rejects such a range — so a stale link to a longer
 * preset shows the retained days instead of an error (see isRangePartial).
 */
export function presetToRange(preset: RangePreset, maxLookbackDays?: number): { from: string; to: string } {
  const presetDays = RANGE_PRESETS.find((p) => p.key === preset)?.days ?? 30;
  const days = maxLookbackDays === undefined ? presetDays : Math.min(presetDays, maxLookbackDays);
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * True when a preset reaches further back than the workspace retains
 * (`maxLookbackDays` from /api/dashboard/limits; undefined while loading).
 * Custom is never "beyond": its date inputs are bounded instead.
 */
export function presetExceedsRetention(preset: RangePreset, maxLookbackDays: number | undefined): boolean {
  if (maxLookbackDays === undefined || preset === 'C') return false;
  const days = RANGE_PRESETS.find((p) => p.key === preset)?.days ?? 0;
  return days > maxLookbackDays;
}

/**
 * True when the selected range starts before the retained data. The request
 * is then limited to the retained days (presetToRange / clampCustomRange), and
 * the filter bar says so. Reachable through an old link or a hand-typed
 * custom date even though the picker no longer offers it.
 */
export function isRangePartial(
  preset: RangePreset,
  customRange: { from: string; to: string },
  maxLookbackDays: number | undefined,
  now: Date = new Date(),
): boolean {
  if (maxLookbackDays === undefined) return false;
  if (preset !== 'C') return presetExceedsRetention(preset, maxLookbackDays);
  // Custom dates are whole UTC days, so allow one day of slack.
  return Date.parse(customRange.from) < now.getTime() - (maxLookbackDays + 1) * DAY_MS;
}

/** Earliest day (yyyy-mm-dd) still inside retention — the lower bound for the custom date inputs. */
export function earliestRetainedDay(maxLookbackDays: number, now: Date = new Date()): string {
  return new Date(now.getTime() - maxLookbackDays * DAY_MS).toISOString().slice(0, 10);
}

/** A custom range pulled inside retention, so the API accepts it. Unchanged when it already fits. */
export function clampCustomRange(
  customRange: { from: string; to: string },
  maxLookbackDays: number | undefined,
  now: Date = new Date(),
): { from: string; to: string } {
  if (!isRangePartial('C', customRange, maxLookbackDays, now) || maxLookbackDays === undefined) return customRange;
  const from = new Date(now.getTime() - maxLookbackDays * DAY_MS);
  // A range lying wholly before retention has nothing left to show: fall back to the retained window.
  const to = Date.parse(customRange.to) > from.getTime() ? customRange.to : now.toISOString();
  return { from: from.toISOString(), to };
}
